import React, { useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { zindex } from 'utils/style/zindex';
import { yellow, orange, gray, exactWhite, black } from 'utils/style/colors';
import LocalIcon from 'ui/LocalIcon';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import UserMixpanel from 'utils/mixpanel/MixpanelClass';

interface RatingFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (rating?: number, description?: string) => void;
  title?: string;
  phase?: string;
}

const starGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 5px rgba(255, 193, 7, 0.5));
  }
  50% {
    filter: drop-shadow(0 0 15px rgba(255, 193, 7, 1));
  }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const textareaSlideIn = keyframes`
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 200px;
    transform: translateY(0);
  }
`;

export const ModalBackdrop = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: ${zindex.modal + 1};
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  animation: ${(props) => (props.isOpen ? fadeIn : 'none')} 0.3s ease-out;
`;

export const ModalContent = styled.div`
  background: ${exactWhite};
  border-radius: 20px;
  padding: 3rem 2.5rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: ${(props) => props.maxWidth};
  width: 90%;
  position: relative;
  animation: ${slideInUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  @media only screen and (max-width: 600px) {
    padding: 2rem 1rem;
  }
`;

export const Title = styled.h2`
  margin: 0 0 2rem 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: ${black};
  text-align: center;
`;

const StarsContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  padding: 1rem;
`;

const StarButton = styled(Button)<{
  isActive: boolean;
  isHovered: boolean;
  index: number;
}>`
  background: none !important;
  box-shadow: none !important;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  position: relative;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform: ${(props) => (props.isHovered ? 'scale(1.2)' : 'scale(1)')};

  &:hover {
    transform: scale(1.1);
    & svg {
      animation: ${starGlow} 0.8s ease-in-out;
    }
  }

  svg {
    width: 3rem;
    height: 3rem;
    transition: all 0.3s ease;
    filter: ${(props) =>
      props.isActive
        ? 'drop-shadow(0 0 8px rgba(255, 193, 7, 0.8))'
        : 'drop-shadow(0 0 0 rgba(255, 193, 7, 0))'};
  }

  ${(props) =>
    props.isActive &&
    css`
      svg {
        fill: ${yellow};
        stroke: ${orange};
        stroke-width: 1;
      }
    `}

  ${(props) =>
    !props.isActive &&
    css`
      svg {
        fill: ${gray};
        opacity: 0.4;
      }
    `}
`;

const DescriptionContainer = styled.div<{ show: boolean }>`
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  animation: ${(props) => (props.show ? textareaSlideIn : 'none')} 0.4s ease-out;
  max-height: ${(props) => (props.show ? '200px' : '0')};
  opacity: ${(props) => (props.show ? 1 : 0)};
  margin-bottom: ${(props) => (props.show ? '1.5rem' : '0')};
`;

const DescriptionLabel = styled.label`
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.95rem;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
  background-color: ${exactWhite};

  &:focus {
    outline: none;
    border-color: ${yellow};
    box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.1);
  }

  &::placeholder {
    color: ${gray};
  }
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, ${yellow} 0%, ${orange} 100%);
  color: var(--exact-white);
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 193, 7, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const StarIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="1.5"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RatingForm: React.FC<RatingFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title = i18n.t('rateYourExperience', { ns: 'general' }),
  phase,
}) => {
  const { t } = useTranslation('general');

  const thisUserId = useSelector((state: RootState) => state.thisUser.userId);

  const [rating, setRating] = useState<number>(0);
  const [hoveredStar, setHoveredStar] = useState<number>(0);
  const [description, setDescription] = useState<string>('');

  const handleStarClick = useCallback((starIndex: number) => {
    setRating(starIndex);
  }, []);

  const handleStarHover = useCallback((starIndex: number) => {
    setHoveredStar(starIndex);
  }, []);

  const handleStarLeave = useCallback(() => {
    setHoveredStar(0);
  }, []);

  const handleSubmit = useCallback(() => {
    const mx = UserMixpanel.getInstance(Number(thisUserId));
    mx.sendUserFeedbackTrack(phase ?? '', rating, description);
    if (rating > 0) {
      onSubmit?.(rating, description.trim() || undefined);
      // Reset form
      setRating(0);
      setDescription('');
      setHoveredStar(0);
      onClose();
    }
  }, [rating, description, onSubmit, onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) return null;

  const showDescription = rating > 0 && rating <= 3;

  return (
    <ModalBackdrop isOpen={isOpen} onClick={handleBackdropClick}>
      <ModalContent
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        <LocalIcon
          iconName="x.svg"
          color={gray}
          onClick={onClose}
          isLink={true}
        />
        <Title>{title}</Title>
        <StarsContainer>
          {[1, 2, 3, 4, 5].map((starIndex) => (
            <StarButton
              key={starIndex}
              isActive={starIndex <= (hoveredStar || rating)}
              isHovered={hoveredStar === starIndex}
              index={starIndex}
              onClick={() => handleStarClick(starIndex)}
              onMouseEnter={() => handleStarHover(starIndex)}
              onMouseLeave={handleStarLeave}
              aria-label={`Rate ${starIndex} star${starIndex > 1 ? 's' : ''}`}
            >
              <StarIcon filled={starIndex <= (hoveredStar || rating)} />
            </StarButton>
          ))}
        </StarsContainer>
        <DescriptionContainer show={showDescription}>
          <DescriptionLabel htmlFor="rating-description">
            {t('pleaseTellUsWhatWeCanImprove')}
          </DescriptionLabel>
          <DescriptionTextarea
            id="rating-description"
            value={description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setDescription(e.target.value)
            }
            placeholder={t('shareYourFeedbackWithUs')}
            rows={4}
          />
        </DescriptionContainer>
        <SubmitButton disabled={rating === 0} onClick={handleSubmit}>
          {t('submitFeedback')}
        </SubmitButton>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default RatingForm;
